import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Info,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Trophy,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Toast, type ToastKind } from "@/components/Toast";
import {
  ValidationDialog,
  type ValidationIssue,
} from "@/components/ValidationDialog";
import { api, type TournamentCategory, type TournamentInput } from "@/lib/api";
import { cn } from "@/lib/utils";

type Draft = {
  name: string;
  slug: string;
  date: string;
  location: string;
  categories: TournamentCategory[];
  formula: string;
  match_duration_min: number;
  guaranteed_matches: number;
  prizes_first: string;
  registration_deadline: string;
  additional_notes: string;
  partner: string;
  sponsors: string[];
  after_party: string;
  description_short: string;
  visual_style: string;
  visual_mood: string;
};

const DEFAULT_CATEGORY = (): TournamentCategory => ({
  name: "",
  level: "open",
  max_teams: 16,
  price_community: 25,
  price_non_community: 30,
  playtomic_link: "",
  time_start: "15:00",
  time_end: "18:00",
  bitly_link: "",
});

const INITIAL_DRAFT: Draft = {
  name: "",
  slug: "",
  date: "",
  location: "Moving Padel Monza",
  categories: [DEFAULT_CATEGORY()],
  formula: "Gironi + Eliminazione Diretta",
  match_duration_min: 15,
  guaranteed_matches: 4,
  prizes_first: "Ricarica Borsellino",
  registration_deadline: "",
  additional_notes: "",
  partner: "Moving Bistrot",
  sponsors: [],
  after_party:
    "Dopo il torneo fermati da Moving Bistrot per un Tie Break di gusto!",
  description_short: "",
  visual_style: "",
  visual_mood: "",
};

const STEPS = [
  { id: 1, label: "Base" },
  { id: 2, label: "Categorie" },
  { id: 3, label: "Formula" },
  { id: 4, label: "Branding" },
  { id: 5, label: "Review" },
];

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);
}

export function draftFromInput(
  input: Partial<TournamentInput>,
  visual?: Record<string, unknown> | null,
): Draft {
  const t = input ?? {};
  const v = visual ?? {};
  return {
    name: t.name ?? "",
    slug: t.slug ?? "",
    date: t.date ?? "",
    location: t.location ?? "Moving Padel Monza",
    categories:
      Array.isArray(t.categories) && t.categories.length > 0
        ? t.categories.map(
            (c): TournamentCategory => ({
              name: c.name ?? "",
              level: c.level ?? "open",
              max_teams: c.max_teams ?? 16,
              price_community: c.price_community ?? 0,
              price_non_community: c.price_non_community ?? 0,
              playtomic_link: c.playtomic_link ?? "",
              time_start: c.time_start ?? t.time_start ?? "15:00",
              time_end: c.time_end ?? t.time_end ?? "18:00",
              bitly_link: c.bitly_link ?? "",
            }),
          )
        : [DEFAULT_CATEGORY()],
    formula: t.formula ?? "Gironi + Eliminazione Diretta",
    match_duration_min: t.match_duration_min ?? 15,
    guaranteed_matches: t.guaranteed_matches ?? 4,
    prizes_first: (t.prizes && (t.prizes as Record<string, string>).first) || "",
    registration_deadline: t.registration_deadline ?? "",
    additional_notes: t.additional_notes ?? "",
    partner: t.partner ?? "Moving Bistrot",
    sponsors: Array.isArray(t.sponsors) ? t.sponsors : [],
    after_party: t.after_party ?? "",
    description_short: t.description_short ?? "",
    visual_style: (t.visual_style as string) ?? (v.style as string) ?? "",
    visual_mood: (t.visual_mood as string) ?? (v.mood as string) ?? "",
  };
}

type TournamentFormProps = {
  /** Se passato, il form è in modalità "edit" e submitta PATCH sul backend. */
  editSlug?: string;
  /** Draft iniziale (usato in edit mode). */
  initialDraft?: Draft;
};

export default function TournamentNew({
  editSlug,
  initialDraft,
}: TournamentFormProps = {}) {
  const isEdit = Boolean(editSlug);
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(initialDraft ?? INITIAL_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(
    null,
  );
  // In edit mode lo slug è già fissato (è l'identità del file YAML)
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [validationOpen, setValidationOpen] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);

  const update = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const onNameChange = (v: string) => {
    setDraft((d) => ({
      ...d,
      name: v,
      slug: slugTouched ? d.slug : slugify(v),
    }));
  };

  const addCategory = () => {
    setDraft((d) => ({ ...d, categories: [...d.categories, DEFAULT_CATEGORY()] }));
  };

  const removeCategory = (idx: number) => {
    setDraft((d) => ({
      ...d,
      categories: d.categories.filter((_, i) => i !== idx),
    }));
  };

  const updateCategory = <K extends keyof TournamentCategory>(
    idx: number,
    key: K,
    value: TournamentCategory[K],
  ) => {
    setDraft((d) => ({
      ...d,
      categories: d.categories.map((c, i) =>
        i === idx ? { ...c, [key]: value } : c,
      ),
    }));
  };

  // Bitly shortening state per categoria
  const [bitlyPending, setBitlyPending] = useState<Record<number, boolean>>({});
  const [bitlyError, setBitlyError] = useState<Record<number, string>>({});
  // Keyword custom editabile dall'utente (sovrascrive il default calcolato)
  const [bitlyKeyword, setBitlyKeyword] = useState<Record<number, string>>({});

  const defaultKeyword = (idx: number) => {
    const cat = draft.categories[idx];
    const parts = [
      slugify(draft.name || draft.slug || "moving").slice(0, 20),
      slugify(cat?.name || `cat-${idx + 1}`).slice(0, 20),
    ].filter(Boolean);
    return parts.join("-");
  };

  const keywordFor = (idx: number) =>
    bitlyKeyword[idx] ?? defaultKeyword(idx);

  const shortenBitlyForCategory = async (idx: number) => {
    const cat = draft.categories[idx];
    if (!cat?.playtomic_link?.trim()) return;
    const keyword = keywordFor(idx).trim();
    if (keyword.length < 4) {
      setBitlyError((e) => ({ ...e, [idx]: "Keyword troppo corta (min 4 char)" }));
      return;
    }

    setBitlyPending((p) => ({ ...p, [idx]: true }));
    setBitlyError((e) => ({ ...e, [idx]: "" }));
    try {
      const res = await api.shortenBitly(cat.playtomic_link.trim(), keyword);
      updateCategory(idx, "bitly_link", res.link);
      setToast({
        kind: "success",
        message: res.custom
          ? `Bitly creato: ${res.link}`
          : `Bitly (fallback): ${res.link}`,
      });
    } catch (e) {
      const msg =
        (e as { body?: { detail?: string }; message?: string })?.body?.detail ??
        (e as Error)?.message ??
        String(e);
      setBitlyError((er) => ({ ...er, [idx]: msg }));
    } finally {
      setBitlyPending((p) => ({ ...p, [idx]: false }));
    }
  };

  // Per gli sponsor multipli: input separato che aggiunge alla lista
  const [sponsorInput, setSponsorInput] = useState("");
  const addSponsor = () => {
    const v = sponsorInput.trim();
    if (!v) return;
    setDraft((d) => ({ ...d, sponsors: [...d.sponsors, v] }));
    setSponsorInput("");
  };
  const removeSponsor = (i: number) => {
    setDraft((d) => ({
      ...d,
      sponsors: d.sponsors.filter((_, idx) => idx !== i),
    }));
  };

  // ──────────────────────────────────────────────────────────────────────
  // Validation
  // ──────────────────────────────────────────────────────────────────────
  const canAdvance = () => {
    // Validazione hard: blocca solo se mancano i campi strettamente obbligatori
    if (step === 1) {
      return (
        draft.name.trim().length > 3 &&
        /^[a-z0-9_]+$/.test(draft.slug) &&
        !!draft.date
      );
    }
    if (step === 2) {
      return (
        draft.categories.length > 0 &&
        draft.categories.every(
          (c) =>
            c.name.trim().length > 0 &&
            c.max_teams > 0 &&
            c.price_community >= 0 &&
            c.price_non_community >= 0,
        )
      );
    }
    if (step === 3) return draft.formula.length > 0;
    return true;
  };

  const collectIssues = (): ValidationIssue[] => {
    const out: ValidationIssue[] = [];
    if (!draft.location.trim())
      out.push({ field: "Sede del torneo", step: 1, hint: "Es. Moving Padel Monza" });
    draft.categories.forEach((c, i) => {
      const label = c.name || `Categoria #${i + 1}`;
      if (!c.time_start)
        out.push({
          field: `Orario inizio · ${label}`,
          step: 2,
          hint: "Serve per flyer/email/landing",
        });
      if (!c.time_end)
        out.push({
          field: `Orario fine · ${label}`,
          step: 2,
        });
      if (!c.playtomic_link)
        out.push({
          field: `Link Playtomic · ${label}`,
          step: 2,
          hint: "Puoi compilarlo dopo l'apertura iscrizioni",
        });
    });
    if (!draft.registration_deadline)
      out.push({
        field: "Scadenza iscrizioni",
        step: 3,
        hint: "Deadline per iscriversi — utile in email/landing",
      });
    if (!draft.description_short.trim())
      out.push({
        field: "Descrizione breve",
        step: 4,
        hint: "Frase di apertura per IG/FB/email",
      });
    if (!draft.visual_style.trim())
      out.push({
        field: "Stile visual (NB2)",
        step: 4,
        hint: "Es. 'foto realistica campo indoor'",
      });
    if (!draft.visual_mood.trim())
      out.push({
        field: "Mood visual (NB2)",
        step: 4,
        hint: "Es. 'primavera energica'",
      });
    return out;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        tournament: {
          name: draft.name,
          slug: draft.slug,
          date: draft.date,
          // Orari a livello tournament: uso quelli della prima categoria come
          // fallback per backward compat con template esistenti.
          time_start: draft.categories[0]?.time_start || "15:00",
          time_end: draft.categories[0]?.time_end || "18:00",
          location: draft.location,
          categories: draft.categories,
          formula: draft.formula,
          match_duration_min: draft.match_duration_min,
          guaranteed_matches: draft.guaranteed_matches,
          prizes: { first: draft.prizes_first },
          registration_deadline: draft.registration_deadline || null,
          partner: draft.partner,
          sponsors: draft.sponsors,
          after_party: draft.after_party,
          description_short: draft.description_short,
          additional_notes: draft.additional_notes,
          visual_style: draft.visual_style,
          visual_mood: draft.visual_mood,
        },
        visual: {
          style: draft.visual_style,
          mood: draft.visual_mood,
        },
      };
      if (isEdit && editSlug) {
        await api.patchTournament(editSlug, payload);
        setToast({
          kind: "success",
          message: `Torneo "${draft.name}" aggiornato ✓`,
        });
        setTimeout(() => nav(`/tournaments/${editSlug}`), 700);
      } else {
        await api.createTournament(payload);
        setToast({
          kind: "success",
          message: `Torneo "${draft.name}" creato ✓`,
        });
        setTimeout(() => nav(`/tournaments/${draft.slug}`), 800);
      }
    } catch (e) {
      const msg =
        (e as { body?: { detail?: string }; message?: string })?.body?.detail ??
        (e as Error)?.message ??
        String(e);
      setToast({ kind: "error", message: `Errore: ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const onCreateClick = () => {
    const found = collectIssues();
    if (found.length === 0) {
      submit();
    } else {
      setIssues(found);
      setValidationOpen(true);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Back */}
      <div>
        <Link
          to={isEdit ? `/tournaments/${editSlug}` : "/tournaments"}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {isEdit ? "Torna al torneo" : "Tutti i tornei"}
        </Link>
      </div>

      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          {isEdit ? (
            <Pencil className="h-6 w-6 text-moving-500" />
          ) : (
            <Trophy className="h-6 w-6 text-moving-500" />
          )}
          {isEdit ? `Modifica torneo · ${draft.name || editSlug}` : "Nuovo torneo"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit
            ? "Modifica i dati base del torneo. Lo slug non è modificabile. Ricorda di rigenerare i testi/locandina se hai cambiato dati usati dalla pipeline."
            : "Compila i 5 step, il sistema genera il YAML + inizializza la pipeline."}
        </p>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-1 overflow-x-auto">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => s.id <= step && setStep(s.id)}
              disabled={s.id > step}
              className={cn(
                "flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium",
                s.id === step
                  ? "bg-moving-500 text-white"
                  : s.id < step
                    ? "bg-moving-100 text-moving-700 hover:bg-moving-200"
                    : "bg-slate-100 text-slate-400",
              )}
            >
              {s.id < step ? (
                <Check className="h-3 w-3" />
              ) : (
                <span className="font-semibold">{s.id}</span>
              )}
              {s.label}
            </button>
            {i < STEPS.length - 1 ? (
              <span className="h-px w-6 bg-slate-200" />
            ) : null}
          </div>
        ))}
      </div>

      {/* ─── STEP 1 · BASE ──────────────────────────────────────────── */}
      {step === 1 ? (
        <Card>
          <CardHeader>
            <CardTitle>Dati base</CardTitle>
          </CardHeader>
          <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="name">Nome torneo *</Label>
              <Input
                id="name"
                value={draft.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="Es. Torneo Spring 2026"
                className="mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="slug">
                Slug {isEdit ? "(non modificabile)" : "*"}
              </Label>
              <Input
                id="slug"
                value={draft.slug}
                onChange={(e) => {
                  if (isEdit) return;
                  setSlugTouched(true);
                  update("slug", slugify(e.target.value));
                }}
                placeholder="torneo_spring_2026"
                readOnly={isEdit}
                className={cn(
                  "mt-1 font-mono",
                  isEdit && "bg-slate-100 cursor-not-allowed",
                )}
              />
              <div className="mt-1 text-xs text-slate-500">
                {isEdit
                  ? "Lo slug è fissato all'identità del file YAML e non può essere cambiato."
                  : "Solo minuscole, numeri, underscore. Usato nei path del sistema."}
              </div>
            </div>
            <div>
              <Label htmlFor="date">Data torneo *</Label>
              <Input
                id="date"
                type="date"
                value={draft.date}
                onChange={(e) => update("date", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="location">Sede</Label>
              <Input
                id="location"
                value={draft.location}
                onChange={(e) => update("location", e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="md:col-span-2 rounded-md border border-court-200 bg-court-50 p-3 text-xs text-court-800">
              <Info className="mr-1 inline h-3.5 w-3.5" />
              Gli orari di inizio e fine sono ora per singola categoria (step 2):
              permette giornate spaccate su più slot.
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* ─── STEP 2 · CATEGORIE ─────────────────────────────────────── */}
      {step === 2 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Categorie ({draft.categories.length})</CardTitle>
              <Button variant="outline" size="sm" onClick={addCategory}>
                <Plus className="h-4 w-4" />
                Aggiungi
              </Button>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            {draft.categories.map((cat, i) => (
              <div
                key={i}
                className="rounded-md border border-slate-200 bg-slate-50 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600">
                    Categoria #{i + 1}
                  </span>
                  {draft.categories.length > 1 ? (
                    <button
                      onClick={() => removeCategory(i)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <Label>Nome *</Label>
                    <Input
                      value={cat.name}
                      onChange={(e) => updateCategory(i, "name", e.target.value)}
                      placeholder="Misto, Maschile, Femminile..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Livello</Label>
                    <Input
                      value={cat.level}
                      onChange={(e) => updateCategory(i, "level", e.target.value)}
                      placeholder="open, 0-2, 2-3, 3-4..."
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Orario inizio *</Label>
                      <Input
                        type="time"
                        value={cat.time_start}
                        onChange={(e) =>
                          updateCategory(i, "time_start", e.target.value)
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Orario fine</Label>
                      <Input
                        type="time"
                        value={cat.time_end}
                        onChange={(e) =>
                          updateCategory(i, "time_end", e.target.value)
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Max squadre</Label>
                    <Input
                      type="number"
                      min={2}
                      value={cat.max_teams}
                      onChange={(e) =>
                        updateCategory(i, "max_teams", parseInt(e.target.value))
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Prezzo community</Label>
                      <Input
                        type="number"
                        min={0}
                        value={cat.price_community}
                        onChange={(e) =>
                          updateCategory(
                            i,
                            "price_community",
                            parseFloat(e.target.value),
                          )
                        }
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Prezzo standard</Label>
                      <Input
                        type="number"
                        min={0}
                        value={cat.price_non_community}
                        onChange={(e) =>
                          updateCategory(
                            i,
                            "price_non_community",
                            parseFloat(e.target.value),
                          )
                        }
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Link Playtomic</Label>
                    <Input
                      value={cat.playtomic_link}
                      onChange={(e) => {
                        updateCategory(i, "playtomic_link", e.target.value);
                        // Se cambi il link, invalida il vecchio bitly
                        if (cat.bitly_link) updateCategory(i, "bitly_link", "");
                      }}
                      placeholder="https://playtomic.com/tournaments/..."
                      className="mt-1 font-mono text-xs"
                    />
                    <div className="mt-1 text-xs text-slate-500">
                      Incolla il link Playtomic. Puoi anche lasciarlo vuoto e compilarlo dopo.
                    </div>
                  </div>

                  {/* Short link Bitly — keyword editabile + bottone Genera esplicito */}
                  {cat.playtomic_link?.trim() ? (
                    <div className="md:col-span-2 rounded-md border border-court-200 bg-court-50 p-3">
                      <div className="mb-2 text-xs font-semibold text-court-900">
                        🔗 Short link Bitly (per tracciare i click)
                      </div>
                      {cat.bitly_link ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <Input
                              value={cat.bitly_link}
                              readOnly
                              className="font-mono text-xs bg-moving-50"
                            />
                            <Button
                              variant="outline"
                              size="md"
                              onClick={() =>
                                navigator.clipboard.writeText(cat.bitly_link)
                              }
                            >
                              Copia
                            </Button>
                            <Button
                              variant="ghost"
                              size="md"
                              onClick={() => {
                                updateCategory(i, "bitly_link", "");
                                setBitlyKeyword((k) => ({
                                  ...k,
                                  [i]: defaultKeyword(i),
                                }));
                              }}
                              title="Rigenera con altra keyword"
                            >
                              Rigenera
                            </Button>
                          </div>
                          <div className="text-xs text-moving-700 flex items-center gap-1.5">
                            <Check className="h-3 w-3" />
                            Short link generato correttamente
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-slate-500">
                              bit.ly/
                            </span>
                            <Input
                              value={keywordFor(i)}
                              onChange={(e) =>
                                setBitlyKeyword((k) => ({
                                  ...k,
                                  [i]: slugify(e.target.value).replace(/_/g, "-"),
                                }))
                              }
                              placeholder="nome-personalizzato"
                              className="font-mono text-xs"
                            />
                            <Button
                              variant="primary"
                              size="md"
                              onClick={() => shortenBitlyForCategory(i)}
                              loading={bitlyPending[i]}
                              disabled={
                                bitlyPending[i] ||
                                keywordFor(i).trim().length < 4
                              }
                            >
                              Genera
                            </Button>
                          </div>
                          <div className="text-xs text-slate-600">
                            Modifica la keyword se vuoi (min 4 caratteri, solo
                            minuscole / numeri / trattini). Default:{" "}
                            <code className="font-mono">{defaultKeyword(i)}</code>.
                          </div>
                          {bitlyError[i] ? (
                            <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                              Errore: {bitlyError[i]}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </CardBody>
        </Card>
      ) : null}

      {/* ─── STEP 3 · FORMULA ───────────────────────────────────────── */}
      {step === 3 ? (
        <Card>
          <CardHeader>
            <CardTitle>Formula &amp; dettagli</CardTitle>
          </CardHeader>
          <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="formula">Formula *</Label>
              <Input
                id="formula"
                value={draft.formula}
                onChange={(e) => update("formula", e.target.value)}
                placeholder="Gironi + Eliminazione Diretta"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="duration">Durata partita (min)</Label>
              <Input
                id="duration"
                type="number"
                min={5}
                value={draft.match_duration_min}
                onChange={(e) =>
                  update("match_duration_min", parseInt(e.target.value))
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="guaranteed">Partite garantite</Label>
              <Input
                id="guaranteed"
                type="number"
                min={1}
                value={draft.guaranteed_matches}
                onChange={(e) =>
                  update("guaranteed_matches", parseInt(e.target.value))
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="prize">Premio 1°</Label>
              <Input
                id="prize"
                value={draft.prizes_first}
                onChange={(e) => update("prizes_first", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="deadline">Scadenza iscrizioni</Label>
              <Input
                id="deadline"
                type="date"
                value={draft.registration_deadline}
                onChange={(e) => update("registration_deadline", e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notes">Altre comunicazioni</Label>
              <Textarea
                id="notes"
                value={draft.additional_notes}
                onChange={(e) => update("additional_notes", e.target.value)}
                placeholder="Note libere da veicolare nei testi: es. 'regola speciale 3° set supertiebreak', 'quiz dopo il torneo', 'porta asciugamano'..."
                rows={5}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-slate-500">
                Questo testo finirà nei prompt per IG/FB/WA/Email. Scrivi
                liberamente, il sistema sceglierà il tono per canale.
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* ─── STEP 4 · BRANDING ──────────────────────────────────────── */}
      {step === 4 ? (
        <Card>
          <CardHeader>
            <CardTitle>Branding &amp; stile</CardTitle>
          </CardHeader>
          <CardBody className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="partner">Partner principale (NON sponsor)</Label>
              <Input
                id="partner"
                value={draft.partner}
                onChange={(e) => update("partner", e.target.value)}
                className="mt-1"
              />
              <div className="mt-1 text-xs text-slate-500">
                Moving Bistrot è il nostro partner: il logo verrà inserito
                accanto alla scritta dell'after-party, non nella sezione sponsor.
              </div>
            </div>

            <div>
              <Label>Sponsor del torneo</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  value={sponsorInput}
                  onChange={(e) => setSponsorInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSponsor();
                    }
                  }}
                  placeholder="Es. Unicar, Diottrica, One Real Estate…"
                />
                <Button variant="outline" size="md" onClick={addSponsor}>
                  <Plus className="h-4 w-4" />
                  Aggiungi
                </Button>
              </div>
              {draft.sponsors.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {draft.sponsors.map((s, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700"
                    >
                      {s}
                      <button
                        onClick={() => removeSponsor(i)}
                        className="text-slate-400 hover:text-red-600"
                        aria-label={`Rimuovi ${s}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div>
              <Label htmlFor="afterparty">After-party</Label>
              <Textarea
                id="afterparty"
                value={draft.after_party}
                onChange={(e) => update("after_party", e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="descr">Descrizione breve (per caption/email)</Label>
              <Textarea
                id="descr"
                value={draft.description_short}
                onChange={(e) => update("description_short", e.target.value)}
                placeholder="Frase di apertura accattivante"
                rows={3}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="style">
                  Stile visual (per NB2)
                  <Sparkles className="ml-1 inline h-3.5 w-3.5 text-moving-500" />
                </Label>
                <Input
                  id="style"
                  value={draft.visual_style}
                  onChange={(e) => update("visual_style", e.target.value)}
                  placeholder="foto realistica, illustrazione, dinamico..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="mood">Mood visual</Label>
                <Input
                  id="mood"
                  value={draft.visual_mood}
                  onChange={(e) => update("visual_mood", e.target.value)}
                  placeholder="primavera energica, autunno cozy..."
                  className="mt-1"
                />
              </div>
            </div>

            <div className="rounded-md border border-court-200 bg-court-50 p-3 text-xs text-court-900">
              <div className="mb-1 flex items-center gap-1.5 font-medium">
                <Info className="h-3.5 w-3.5" />
                Immagini di riferimento per NB2
              </div>
              <p>
                L'upload delle immagini di riferimento (per guidare lo stile
                della locandina) è disponibile <span className="font-medium">
                subito dopo aver creato il torneo</span>. Appena clicchi "Crea
                torneo" al prossimo step, verrai reindirizzato alla pagina del
                dettaglio, dove troverai una card "Immagini di riferimento"
                con drag &amp; drop.
              </p>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* ─── STEP 5 · REVIEW ────────────────────────────────────────── */}
      {step === 5 ? (
        <Card>
          <CardHeader>
            <CardTitle>Review</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
              <Field label="Nome" value={draft.name} />
              <Field label="Slug" value={draft.slug} mono />
              <Field label="Data" value={draft.date} />
              <Field label="Sede" value={draft.location} />
              <Field label="Formula" value={draft.formula} />
              <Field
                label="Partite garantite"
                value={String(draft.guaranteed_matches)}
              />
              <Field label="Partner" value={draft.partner} />
              <Field
                label="Sponsor"
                value={
                  draft.sponsors.length > 0
                    ? draft.sponsors.join(", ")
                    : "— nessuno"
                }
              />
            </dl>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase text-slate-500">
                Categorie ({draft.categories.length})
              </div>
              <ul className="space-y-1 text-sm">
                {draft.categories.map((c, i) => (
                  <li key={i} className="rounded-md bg-slate-50 px-3 py-2">
                    <span className="font-medium">{c.name || "(senza nome)"}</span>
                    <span className="text-slate-500">
                      {" "}
                      · liv. {c.level} · {c.time_start || "—"}
                      {c.time_end ? `–${c.time_end}` : ""} · max {c.max_teams}{" "}
                      squadre · {c.price_community}€ comm / {c.price_non_community}€ std
                    </span>
                    {c.playtomic_link ? (
                      <div className="mt-0.5 truncate font-mono text-[11px] text-court-600">
                        {c.playtomic_link}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>

            {draft.additional_notes ? (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase text-slate-500">
                  Altre comunicazioni
                </div>
                <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                  {draft.additional_notes}
                </p>
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {/* Nav buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1}
        >
          <ArrowLeft className="h-4 w-4" />
          Indietro
        </Button>
        {step < 5 ? (
          <Button
            variant="primary"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canAdvance()}
          >
            Avanti
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="primary" onClick={onCreateClick} loading={submitting}>
            <Check className="h-4 w-4" />
            {isEdit ? "Salva modifiche" : "Crea torneo"}
          </Button>
        )}
      </div>

      <ValidationDialog
        open={validationOpen}
        issues={issues}
        loading={submitting}
        onClose={() => setValidationOpen(false)}
        onReviewAt={(s) => {
          setValidationOpen(false);
          setStep(s);
        }}
        onProceed={() => {
          setValidationOpen(false);
          submit();
        }}
      />

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd
        className={cn(
          "text-sm text-slate-900",
          mono ? "font-mono text-xs" : "",
        )}
      >
        {value || "—"}
      </dd>
    </div>
  );
}
